import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { getAllCountries } from "@store/Store/Data/Countries/CountriesSelectors";
import loadAllCountries from "@store/Store/Data/Countries/Handlers/LoadAllCountries";
import { getFieldValue } from "@store/Store/Pages/CreateAccountForm/CreateAccountFormSelectors";
import setFieldValue from "@store/Store/Pages/CreateAccountForm/Handlers/SetFieldValue";
import useFieldValidation from "@store/Store/Pages/CreateAccountForm/Hooks/useFieldValidation";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { formTextFieldFieldDefinitions } from "@store/Widgets/CreateAccount/FieldTypes/FormTextField";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const fieldKey = "country";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: StoreApplicationState) => ({
    value: getFieldValue(state, fieldKey),
    countries: getAllCountries(state),
});

const mapDispatchToProps = {
    setFieldValue,
    loadAllCountries,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateAccountCountryStyles {
    select?: SelectPresentationProps;
}

export const createAccountCountryStyles: CreateAccountCountryStyles = {};

const styles = createAccountCountryStyles;

const CreateAccountCountry: FC<Props> = ({ fields, countries, value, setFieldValue, loadAllCountries }) => {
    const { label, hintText, isRequired } = fields;
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (!countries) {
            loadAllCountries();
        }
    }, []);

    const validate = () => {
        const message = isRequired && !value ? (siteMessage("Field_Required", label) as string) : "";
        setErrorMessage(message);
        return !message;
    };

    useFieldValidation(fieldKey, value, validate);

    const changeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        const field = event.target;
        setFieldValue({ key: fieldKey, value: field.value });
        setFieldValue({ key: "countryName", value: field.value ? field.options[field.selectedIndex].text : "" });
    };

    return (
        <Select
            {...styles.select}
            label={label}
            required={isRequired}
            value={value || ""}
            error={errorMessage}
            onChange={changeHandler}
        >
            <option value="">{hintText}</option>
            {countries?.map(country => (
                <option key={country.id} value={country.id}>
                    {country.name}
                </option>
            ))}
        </Select>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CreateAccountCountry),
    definition: {
        group: "Create Account" as any,
        displayName: "Country",
        icon: "input-pipe",
        fieldDefinitions: formTextFieldFieldDefinitions("Country", "Select a country", true) as FieldDefinition[],
    },
};

export default widgetModule;
